import client from './client'
import { fetchAllPages } from './paging'
import {
  TapPhoneScreen,
  TapInterview,
  TapInterviewAttendee,
  TapInterviewFeedback,
  TapAssessment,
  TapReferenceCheck,
  TapOffer,
  TapDocument,
  TapNote,
  TapSkill,
  TapApplicationSkill,
  TapApplicationCertification,
  LabLookupValue,
  TapStageSla,
  TapScorecardTemplate,
  TapScorecardCompetency,
  TapDashboard,
  TapInterviewResourceLink,
} from '../types/index'

// ---------------------------------------------------------------------------
// WI #337: these collections are read through `fetchAllPages` (api/paging.ts).
//
// The previous helper, `unwrapList`, pulled `items` out of the envelope and
// threw `total` away, and none of these fetchers sent any params — so every
// TAP tab rendered the server's default first 20 rows and had no way to know
// there were more. An application with 25 emails showed 20 and looked
// complete. `fetchAllPages` walks the pages and still tolerates a bare array
// from the handful of endpoints that never adopted the envelope (Bug #149).
// ---------------------------------------------------------------------------

// Strip undefined values from a payload before POST/PATCH so Pydantic applies
// its own defaults instead of erroring on `null` where the field is typed
// Optional[str] with a non-None default.
const prune = (payload: Record<string, unknown>): Record<string, unknown> => {
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k]
  })
  return payload
}

// ---------------------------------------------------------------------------
// Phone Screens (Feature #154)
// ---------------------------------------------------------------------------

interface RawPhoneScreen {
  id: string
  application_id: string
  scheduled_at?: string | null
  duration_minutes?: number | null
  conducted_by_user_id?: string | null
  location_or_link?: string | null
  preparation_notes?: string | null
  outcome_summary?: string | null
  recommendation?: string | null
  cancelled_at?: string | null
  is_active: boolean
  created_at: string
}

const mapPhoneScreen = (r: RawPhoneScreen): TapPhoneScreen => ({
  id: r.id,
  applicationId: r.application_id,
  scheduledAt: r.scheduled_at ?? undefined,
  durationMinutes: r.duration_minutes ?? undefined,
  conductedByUserId: r.conducted_by_user_id ?? undefined,
  locationOrLink: r.location_or_link ?? undefined,
  preparationNotes: r.preparation_notes ?? undefined,
  outcomeSummary: r.outcome_summary ?? undefined,
  recommendation: r.recommendation ?? undefined,
  cancelledAt: r.cancelled_at ?? undefined,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
})

export interface PhoneScreenCreateInput {
  scheduledAt?: string
  durationMinutes?: number
  conductedByUserId?: string
  locationOrLink?: string
  preparationNotes?: string
}

export interface PhoneScreenUpdateInput extends PhoneScreenCreateInput {
  outcomeSummary?: string
  recommendation?: string
}

export const getPhoneScreens = async (applicationId: string): Promise<TapPhoneScreen[]> => {
  const rows = await fetchAllPages<RawPhoneScreen>(`/tap/applications/${applicationId}/phone-screens`)
  return rows.map(mapPhoneScreen)
}

export const createPhoneScreen = async (
  applicationId: string,
  data: PhoneScreenCreateInput,
): Promise<TapPhoneScreen> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/phone-screens`,
    prune({
      application_id: applicationId,
      scheduled_at: data.scheduledAt,
      duration_minutes: data.durationMinutes,
      conducted_by_user_id: data.conductedByUserId,
      location_or_link: data.locationOrLink,
      preparation_notes: data.preparationNotes,
    }),
  )
  return mapPhoneScreen(response.data)
}

export const updatePhoneScreen = async (
  applicationId: string,
  id: string,
  data: PhoneScreenUpdateInput,
): Promise<TapPhoneScreen> => {
  const response = await client.patch(
    `/tap/applications/${applicationId}/phone-screens/${id}`,
    prune({
      scheduled_at: data.scheduledAt,
      duration_minutes: data.durationMinutes,
      conducted_by_user_id: data.conductedByUserId,
      location_or_link: data.locationOrLink,
      preparation_notes: data.preparationNotes,
      outcome_summary: data.outcomeSummary,
      recommendation: data.recommendation,
    }),
  )
  return mapPhoneScreen(response.data)
}

export const cancelPhoneScreen = async (
  applicationId: string,
  id: string,
): Promise<TapPhoneScreen> => {
  const response = await client.post(`/tap/applications/${applicationId}/phone-screens/${id}/cancel`)
  return mapPhoneScreen(response.data)
}

// ---------------------------------------------------------------------------
// Interviews (Feature #155)
// ---------------------------------------------------------------------------

// Feature #214: raw attendee shape from the backend.
interface RawAttendee {
  id: string
  user_id: string
  display_name?: string | null
  is_active: boolean
}

const mapAttendee = (r: RawAttendee): TapInterviewAttendee => ({
  id: r.id,
  userId: r.user_id,
  displayName: r.display_name ?? undefined,
  isActive: Boolean(r.is_active),
})

interface RawInterview {
  id: string
  application_id: string
  interview_type: string
  scheduled_at?: string | null
  duration_minutes?: number | null
  location_or_link?: string | null
  preparation_notes?: string | null
  scorecard_template_id?: string | null
  cancelled_at?: string | null
  is_active: boolean
  created_at: string
  // Feature #214: attendees resolved by backend.
  attendees?: RawAttendee[] | null
}

const mapInterview = (r: RawInterview): TapInterview => ({
  id: r.id,
  applicationId: r.application_id,
  interviewType: r.interview_type,
  scheduledAt: r.scheduled_at ?? undefined,
  durationMinutes: r.duration_minutes ?? undefined,
  locationOrLink: r.location_or_link ?? undefined,
  preparationNotes: r.preparation_notes ?? undefined,
  scorecardTemplateId: r.scorecard_template_id ?? undefined,
  cancelledAt: r.cancelled_at ?? undefined,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
  attendees: r.attendees ? r.attendees.map(mapAttendee) : undefined,
})

export interface InterviewCreateInput {
  interviewType: string
  scheduledAt?: string
  durationMinutes?: number
  locationOrLink?: string
  preparationNotes?: string
  scorecardTemplateId?: string
  interviewerUserIds?: string[]
  // Feature #214: TPL staff attending the interview, supplied at creation
  // time so we don't have to chain N follow-up POSTs to /attendees.
  attendeeUserIds?: string[]
}

export interface InterviewUpdateInput {
  interviewType?: string
  scheduledAt?: string
  durationMinutes?: number
  locationOrLink?: string
  preparationNotes?: string
  scorecardTemplateId?: string
}

export interface ScorecardResponseInput {
  competencyId: string
  score: number
  comment?: string
}

export interface InterviewFeedbackInput {
  recommendation?: string
  score?: number
  feedback?: string
  // Feature #177: per-competency ratings + the template they were scored
  // against. Both are optional so the legacy free-form feedback flow keeps
  // working.
  scorecardTemplateId?: string
  scorecardResponses?: ScorecardResponseInput[]
}

interface RawInterviewFeedback {
  id: string
  interview_id: string
  interviewer_user_id?: string | null
  recommendation?: string | null
  score?: number | null
  feedback?: string | null
  is_active: boolean
  created_at: string
  // Feature #177: scoring fields (optional; only present when the feedback
  // was created by the Score Interview flow).
  total_score?: number | null
  scorecard_template_id?: string | null
  responses?: { competency_id: string; score: number; comment?: string | null }[] | null
}

const mapFeedback = (r: RawInterviewFeedback): TapInterviewFeedback => ({
  id: r.id,
  interviewId: r.interview_id,
  interviewerUserId: r.interviewer_user_id ?? undefined,
  recommendation: r.recommendation ?? undefined,
  score: r.score ?? undefined,
  feedback: r.feedback ?? undefined,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
  totalScore: r.total_score ?? undefined,
  scorecardTemplateId: r.scorecard_template_id ?? undefined,
  responses: (r.responses ?? []).map((rsp) => ({
    competencyId: rsp.competency_id,
    score: rsp.score,
    comment: rsp.comment ?? undefined,
  })),
})

export const getInterviews = async (applicationId: string): Promise<TapInterview[]> => {
  const rows = await fetchAllPages<RawInterview>(`/tap/applications/${applicationId}/interviews`)
  return rows.map(mapInterview)
}

export const createInterview = async (
  applicationId: string,
  data: InterviewCreateInput,
): Promise<TapInterview> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/interviews`,
    prune({
      application_id: applicationId,
      interview_type: data.interviewType,
      scheduled_at: data.scheduledAt,
      duration_minutes: data.durationMinutes,
      location_or_link: data.locationOrLink,
      preparation_notes: data.preparationNotes,
      scorecard_template_id: data.scorecardTemplateId,
      interviewer_user_ids: data.interviewerUserIds,
      attendee_user_ids: data.attendeeUserIds,
    }),
  )
  return mapInterview(response.data)
}

export const updateInterview = async (
  applicationId: string,
  id: string,
  data: InterviewUpdateInput,
): Promise<TapInterview> => {
  const response = await client.patch(
    `/tap/applications/${applicationId}/interviews/${id}`,
    prune({
      interview_type: data.interviewType,
      scheduled_at: data.scheduledAt,
      duration_minutes: data.durationMinutes,
      location_or_link: data.locationOrLink,
      preparation_notes: data.preparationNotes,
      scorecard_template_id: data.scorecardTemplateId,
    }),
  )
  return mapInterview(response.data)
}

export const cancelInterview = async (
  applicationId: string,
  id: string,
): Promise<TapInterview> => {
  const response = await client.post(`/tap/applications/${applicationId}/interviews/${id}/cancel`)
  return mapInterview(response.data)
}

export const submitInterviewFeedback = async (
  applicationId: string,
  interviewId: string,
  data: InterviewFeedbackInput,
): Promise<TapInterviewFeedback> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/interviews/${interviewId}/feedback`,
    prune({
      interview_id: interviewId,
      recommendation: data.recommendation,
      score: data.score,
      feedback: data.feedback,
      // Feature #177: when scoring against a template, send the template id
      // and per-competency ratings so the server can compute the 0-100 total
      // and persist the responses.
      scorecard_template_id: data.scorecardTemplateId,
      scorecard_responses: data.scorecardResponses?.map((r) => ({
        competency_id: r.competencyId,
        score: r.score,
        comment: r.comment,
      })),
    }),
  )
  return mapFeedback(response.data)
}

// Feature #177: fetch the active feedback (score) for an interview, or null
// if the interview hasn't been scored yet. Used by the InterviewsTab to
// decide between "Score interview" and "Change score" labels and to prefill
// the modal with existing per-competency ratings.
export const getInterviewFeedback = async (
  applicationId: string,
  interviewId: string,
): Promise<TapInterviewFeedback | null> => {
  const response = await client.get(
    `/tap/applications/${applicationId}/interviews/${interviewId}/feedback`,
  )
  if (!response.data) return null
  return mapFeedback(response.data)
}

// ---------------------------------------------------------------------------
// Feature #200: Interview Resource Links
// ---------------------------------------------------------------------------

interface RawResourceLink {
  id: string
  interview_id: string
  label: string
  url: string
  is_active: boolean
  created_at: string
}

const mapResourceLink = (r: RawResourceLink): TapInterviewResourceLink => ({
  id: r.id,
  interviewId: r.interview_id,
  label: r.label,
  url: r.url,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
})

export const getInterviewLinks = async (
  applicationId: string,
  interviewId: string,
): Promise<TapInterviewResourceLink[]> => {
  const response = await client.get(
    `/tap/applications/${applicationId}/interviews/${interviewId}/links`,
  )
  return ((response.data || []) as RawResourceLink[]).map(mapResourceLink)
}

export const createInterviewLink = async (
  applicationId: string,
  interviewId: string,
  label: string,
  url: string,
): Promise<TapInterviewResourceLink> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/interviews/${interviewId}/links`,
    { label, url },
  )
  return mapResourceLink(response.data)
}

export const deleteInterviewLink = async (
  applicationId: string,
  interviewId: string,
  linkId: string,
): Promise<void> => {
  await client.delete(
    `/tap/applications/${applicationId}/interviews/${interviewId}/links/${linkId}`,
  )
}

// ---------------------------------------------------------------------------
// Feature #214: Interview Attendees
// ---------------------------------------------------------------------------

export const addInterviewAttendee = async (
  applicationId: string,
  interviewId: string,
  userId: string,
): Promise<TapInterviewAttendee> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/interviews/${interviewId}/attendees`,
    { user_id: userId },
  )
  return mapAttendee(response.data)
}

export const removeInterviewAttendee = async (
  applicationId: string,
  interviewId: string,
  attendeeId: string,
): Promise<void> => {
  await client.delete(
    `/tap/applications/${applicationId}/interviews/${interviewId}/attendees/${attendeeId}`,
  )
}

// ---------------------------------------------------------------------------
// Assessment (Feature #156)
// ---------------------------------------------------------------------------

interface RawAssessment {
  id: string
  application_id: string
  role_suitability?: string | null
  determined_salary_min?: number | null
  determined_salary_max?: number | null
  salary_currency?: string | null
  salary_unit?: string | null
  determined_employment_type?: string | null
  line_manager_user_id?: string | null
  debrief_notes?: string | null
  buy_rate?: number | null
  cost_rate?: number | null
  sell_rate?: number | null
  // Feature #194: proposed/max rate split.
  rate_proposed?: number | null
  buy_rate_proposed?: number | null
  buy_rate_max?: number | null
  cost_rate_proposed?: number | null
  cost_rate_max?: number | null
  sell_rate_proposed?: number | null
  sell_rate_max?: number | null
  approval_status?: string | null
  approver_user_id?: string | null
  // Bug #215: resolved display names.
  line_manager_display_name?: string | null
  approver_display_name?: string | null
  // Feature #229: live-computed rate bundle.
  calculated_rates?: {
    employment_type_canonical?: string | null
    proposed?: RawCalculatedRateSet | null
    max?: RawCalculatedRateSet | null
    settings_id?: string | null
    settings_fy_label?: string | null
  } | null
  is_active: boolean
  created_at: string
}

interface RawCalculatedRateSet {
  cost_per_hour?: number | null
  buy_per_hour?: number | null
  sell_per_hour?: number | null
  cost_per_day?: number | null
  buy_per_day?: number | null
  sell_per_day?: number | null
}

const mapCalculatedRateSet = (r: RawCalculatedRateSet | null | undefined) => {
  if (!r) return undefined
  return {
    costPerHour: r.cost_per_hour ?? undefined,
    buyPerHour: r.buy_per_hour ?? undefined,
    sellPerHour: r.sell_per_hour ?? undefined,
    costPerDay: r.cost_per_day ?? undefined,
    buyPerDay: r.buy_per_day ?? undefined,
    sellPerDay: r.sell_per_day ?? undefined,
  }
}

const mapAssessment = (r: RawAssessment): TapAssessment => ({
  id: r.id,
  applicationId: r.application_id,
  roleSuitability: r.role_suitability ?? undefined,
  determinedSalaryMin: r.determined_salary_min ?? undefined,
  determinedSalaryMax: r.determined_salary_max ?? undefined,
  salaryCurrency: r.salary_currency ?? undefined,
  salaryUnit: r.salary_unit ?? undefined,
  determinedEmploymentType: r.determined_employment_type ?? undefined,
  lineManagerUserId: r.line_manager_user_id ?? undefined,
  debriefNotes: r.debrief_notes ?? undefined,
  buyRate: r.buy_rate ?? undefined,
  costRate: r.cost_rate ?? undefined,
  sellRate: r.sell_rate ?? undefined,
  // Feature #194: proposed/max rate split.
  rateProposed: r.rate_proposed ?? undefined,
  buyRateProposed: r.buy_rate_proposed ?? undefined,
  buyRateMax: r.buy_rate_max ?? undefined,
  costRateProposed: r.cost_rate_proposed ?? undefined,
  costRateMax: r.cost_rate_max ?? undefined,
  sellRateProposed: r.sell_rate_proposed ?? undefined,
  sellRateMax: r.sell_rate_max ?? undefined,
  approvalStatus: r.approval_status ?? undefined,
  approverUserId: r.approver_user_id ?? undefined,
  // Bug #215: resolved display names.
  lineManagerDisplayName: r.line_manager_display_name ?? undefined,
  approverDisplayName: r.approver_display_name ?? undefined,
  // Feature #229: live-computed rate bundle.
  calculatedRates: r.calculated_rates ? {
    employmentTypeCanonical: (r.calculated_rates.employment_type_canonical ?? undefined) as
      | 'FullTime' | 'PartTime' | 'Casual' | 'Contractor' | undefined,
    proposed: mapCalculatedRateSet(r.calculated_rates.proposed),
    max: mapCalculatedRateSet(r.calculated_rates.max),
    settingsId: r.calculated_rates.settings_id ?? undefined,
    settingsFyLabel: r.calculated_rates.settings_fy_label ?? undefined,
  } : undefined,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
})

export interface AssessmentInput {
  roleSuitability?: string
  determinedSalaryMin?: number
  determinedSalaryMax?: number
  salaryCurrency?: string
  salaryUnit?: string
  determinedEmploymentType?: string
  lineManagerUserId?: string
  debriefNotes?: string
  buyRate?: number
  costRate?: number
  sellRate?: number
  // Feature #194: proposed/max rate split.
  rateProposed?: number
  buyRateProposed?: number
  buyRateMax?: number
  costRateProposed?: number
  costRateMax?: number
  sellRateProposed?: number
  sellRateMax?: number
}

const toAssessmentPayload = (data: AssessmentInput, includeApplicationId?: string): Record<string, unknown> =>
  prune({
    application_id: includeApplicationId,
    role_suitability: data.roleSuitability,
    determined_salary_min: data.determinedSalaryMin,
    determined_salary_max: data.determinedSalaryMax,
    salary_currency: data.salaryCurrency,
    salary_unit: data.salaryUnit,
    determined_employment_type: data.determinedEmploymentType,
    line_manager_user_id: data.lineManagerUserId,
    debrief_notes: data.debriefNotes,
    buy_rate: data.buyRate,
    cost_rate: data.costRate,
    sell_rate: data.sellRate,
    // Feature #194: proposed/max rate split.
    rate_proposed: data.rateProposed,
    buy_rate_proposed: data.buyRateProposed,
    buy_rate_max: data.buyRateMax,
    cost_rate_proposed: data.costRateProposed,
    cost_rate_max: data.costRateMax,
    sell_rate_proposed: data.sellRateProposed,
    sell_rate_max: data.sellRateMax,
  })

export const getAssessment = async (applicationId: string): Promise<TapAssessment | null> => {
  try {
    const response = await client.get(`/tap/applications/${applicationId}/assessment`)
    return mapAssessment(response.data)
  } catch {
    return null
  }
}
export const createAssessment = async (
  applicationId: string,
  data: AssessmentInput,
): Promise<TapAssessment> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/assessment`,
    toAssessmentPayload(data, applicationId),
  )
  return mapAssessment(response.data)
}
export const updateAssessment = async (
  applicationId: string,
  data: AssessmentInput,
): Promise<TapAssessment> => {
  const response = await client.patch(
    `/tap/applications/${applicationId}/assessment`,
    toAssessmentPayload(data),
  )
  return mapAssessment(response.data)
}
export const requestAssessmentApproval = async (
  applicationId: string,
  approverUserId: string,
): Promise<void> => {
  await client.post(
    `/tap/applications/${applicationId}/assessment/approval-request`,
    { approver_user_id: approverUserId },
  )
}
// Retain legacy alias so older callers continue to compile.
export const requestApproval = requestAssessmentApproval

export const submitAssessmentApprovalDecision = async (
  applicationId: string,
  decision: 'Approved' | 'Rejected',
  note?: string,
): Promise<TapAssessment> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/assessment/approval-decision`,
    prune({ decision, note }),
  )
  return mapAssessment(response.data)
}

export const getScoreComparison = async (applicationId: string): Promise<unknown> => {
  const response = await client.get(`/tap/applications/${applicationId}/assessment/score-comparison`)
  return response.data
}

// ---------------------------------------------------------------------------
// Reference Checks (Feature #157)
// ---------------------------------------------------------------------------

interface RawReferenceCheck {
  id: string
  application_id: string
  referee_name: string
  referee_relationship?: string | null
  referee_organisation?: string | null
  referee_job_title?: string | null
  referee_email?: string | null
  contact_method?: string | null
  feedback?: string | null
  outcome?: string | null
  is_active: boolean
  created_at: string
  // Feature #219: Typeform send/response timestamps + response id.
  typeform_sent_at?: string | null
  typeform_completed_at?: string | null
  typeform_response_id?: string | null
}

const mapReferenceCheck = (r: RawReferenceCheck): TapReferenceCheck => ({
  id: r.id,
  applicationId: r.application_id,
  refereeName: r.referee_name,
  refereeRelationship: r.referee_relationship ?? undefined,
  refereeOrganisation: r.referee_organisation ?? undefined,
  refereeJobTitle: r.referee_job_title ?? undefined,
  refereeEmail: r.referee_email ?? undefined,
  contactMethod: r.contact_method ?? undefined,
  feedback: r.feedback ?? undefined,
  outcome: r.outcome ?? undefined,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
  typeformSentAt: r.typeform_sent_at ?? undefined,
  typeformCompletedAt: r.typeform_completed_at ?? undefined,
  typeformResponseId: r.typeform_response_id ?? undefined,
})

export interface ReferenceCheckInput {
  refereeName: string
  refereeRelationship?: string
  refereeOrganisation?: string
  refereeJobTitle?: string
  refereeEmail?: string
  contactMethod?: string
  feedback?: string
  outcome?: string
}

export const getReferenceChecks = async (applicationId: string): Promise<TapReferenceCheck[]> => {
  const rows = await fetchAllPages<RawReferenceCheck>(`/tap/applications/${applicationId}/reference-checks`)
  return rows.map(mapReferenceCheck)
}

export const createReferenceCheck = async (
  applicationId: string,
  data: ReferenceCheckInput,
): Promise<TapReferenceCheck> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/reference-checks`,
    prune({
      application_id: applicationId,
      referee_name: data.refereeName,
      referee_relationship: data.refereeRelationship,
      referee_organisation: data.refereeOrganisation,
      referee_job_title: data.refereeJobTitle,
      referee_email: data.refereeEmail,
      contact_method: data.contactMethod,
      feedback: data.feedback,
      outcome: data.outcome,
    }),
  )
  return mapReferenceCheck(response.data)
}

export const updateReferenceCheck = async (
  applicationId: string,
  id: string,
  data: Partial<ReferenceCheckInput>,
): Promise<TapReferenceCheck> => {
  const response = await client.patch(
    `/tap/applications/${applicationId}/reference-checks/${id}`,
    prune({
      referee_name: data.refereeName,
      referee_relationship: data.refereeRelationship,
      referee_organisation: data.refereeOrganisation,
      referee_job_title: data.refereeJobTitle,
      referee_email: data.refereeEmail,
      contact_method: data.contactMethod,
      feedback: data.feedback,
      outcome: data.outcome,
    }),
  )
  return mapReferenceCheck(response.data)
}

// Feature #219: send / resend the Reference Check Typeform invite. Server
// returns the updated reference-check row with refreshed typeformSentAt.
export const sendReferenceTypeform = async (
  applicationId: string,
  id: string,
): Promise<TapReferenceCheck> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/reference-checks/${id}/send-typeform`,
  )
  return mapReferenceCheck(response.data)
}

// Feature #219: fetch the Q&A for a completed reference-check Typeform
// response. Same shape as the application-side viewer (Feature #201) so
// the frontend can reuse the same modal layout.
export interface TapTypeformSubmission {
  submittedAt?: string
  responseUrl?: string
  items: { question: string; answer?: string; fieldType?: string }[]
}

interface RawTypeformSubmission {
  submitted_at?: string | null
  response_url?: string | null
  items?: { question: string; answer?: string | null; field_type?: string | null }[]
}

export const getReferenceTypeformSubmission = async (
  applicationId: string,
  id: string,
): Promise<TapTypeformSubmission> => {
  const response = await client.get<RawTypeformSubmission>(
    `/tap/applications/${applicationId}/reference-checks/${id}/typeform-submission`,
  )
  const raw = response.data || {}
  return {
    submittedAt: raw.submitted_at ?? undefined,
    responseUrl: raw.response_url ?? undefined,
    items: (raw.items ?? []).map((it) => ({
      question: it.question,
      answer: it.answer ?? undefined,
      fieldType: it.field_type ?? undefined,
    })),
  }
}

// ---------------------------------------------------------------------------
// Offers (Feature #158)
// ---------------------------------------------------------------------------

interface RawOffer {
  id: string
  application_id: string
  verbal_offer_result?: string | null
  salary?: number | null
  salary_unit?: string | null
  employment_type?: string | null
  location?: string | null
  approval_status?: string | null
  is_active: boolean
  created_at: string
}

const mapOffer = (r: RawOffer): TapOffer => ({
  id: r.id,
  applicationId: r.application_id,
  verbalOfferResult: r.verbal_offer_result ?? undefined,
  salary: r.salary ?? undefined,
  salaryUnit: r.salary_unit ?? undefined,
  employmentType: r.employment_type ?? undefined,
  location: r.location ?? undefined,
  approvalStatus: r.approval_status ?? undefined,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
})

export interface OfferInput {
  verbalOfferResult?: string
  salary?: number
  salaryUnit?: string
  employmentType?: string
  location?: string
}

export const getOffers = async (applicationId: string): Promise<TapOffer[]> => {
  const rows = await fetchAllPages<RawOffer>(`/tap/applications/${applicationId}/offers`)
  return rows.map(mapOffer)
}

export const createOffer = async (applicationId: string, data: OfferInput): Promise<TapOffer> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/offers`,
    prune({
      application_id: applicationId,
      verbal_offer_result: data.verbalOfferResult,
      salary: data.salary,
      salary_unit: data.salaryUnit,
      employment_type: data.employmentType,
      location: data.location,
    }),
  )
  return mapOffer(response.data)
}

// ---------------------------------------------------------------------------
// Documents (Feature #161)
// ---------------------------------------------------------------------------

interface RawDocument {
  id: string
  application_id: string
  kind: string
  filename: string
  content_type?: string | null
  size_bytes?: number | null
  storage_url: string
  version: number
  uploaded_by?: string | null
  uploaded_at?: string | null
  is_active: boolean
}

const mapDocument = (r: RawDocument): TapDocument => ({
  id: r.id,
  applicationId: r.application_id,
  kind: r.kind,
  filename: r.filename,
  contentType: r.content_type ?? undefined,
  sizeBytes: r.size_bytes ?? undefined,
  storageUrl: r.storage_url,
  version: r.version,
  uploadedBy: r.uploaded_by ?? undefined,
  uploadedAt: r.uploaded_at ?? undefined,
  isActive: Boolean(r.is_active),
})

export interface DocumentCreateInput {
  kind: string
  filename: string
  contentType?: string
  sizeBytes?: number
  storageUrl: string
}

export const getDocuments = async (applicationId: string): Promise<TapDocument[]> => {
  const rows = await fetchAllPages<RawDocument>(`/tap/applications/${applicationId}/documents`)
  return rows.map(mapDocument)
}

export const createDocument = async (
  applicationId: string,
  data: DocumentCreateInput,
): Promise<TapDocument> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/documents`,
    prune({
      application_id: applicationId,
      kind: data.kind,
      filename: data.filename,
      content_type: data.contentType,
      size_bytes: data.sizeBytes,
      storage_url: data.storageUrl,
    }),
  )
  return mapDocument(response.data)
}

// Bug #211: prefer the backend /content proxy so users without direct Azure
// AD access to the blob storage account can still view files. The proxy
// fetches the blob server-side using managed identity and streams it back.
//
// Bug #227: the proxy can fail (the endpoint may be missing on a partially-
// rolled deployment, or the auth dependency may reject the token for reasons
// outside our control). Rather than blocking document access entirely —
// which caused a hard regression for admins who previously opened blob URLs
// directly — fall back to the raw storage URL on any proxy failure. Users
// with Azure AD blob access (typically platform admins) get their files; the
// proxy still handles non-admin reviewers when it works.
//
// Signature kept backwards-compatible: storageUrl is still accepted for the
// local-fallback path, but the preferred path is applicationId + documentId.
export const fetchDocumentBlob = async (
  storageUrl: string,
  applicationId?: string,
  documentId?: string,
): Promise<string> => {
  // Preferred path: use the proxy endpoint (Bug #211).
  if (applicationId && documentId) {
    try {
      const response = await client.get(
        `/tap/applications/${applicationId}/documents/${documentId}/content`,
        // skipAuthRedirect: a 401 on this endpoint must NOT redirect the
        // logged-in reviewer to /login — we have a fallback path below.
        // The custom axios config flag is declared in client.ts (Bug #227).
        { responseType: 'blob', skipAuthRedirect: true },
      )
      return URL.createObjectURL(response.data as Blob)
    } catch (err) {
      // Bug #227: proxy unreachable — fall through to the legacy paths
      // below so admins can still open documents via the direct storage URL.
      console.warn('[tapResources] document /content proxy failed; falling back to direct URL', err)
    }
  }
  // Legacy local-fallback: backend-served disk file with a relative /api/v1
  // URL. Fetch via the authenticated client because the FileResponse
  // endpoint is bearer-gated.
  if (storageUrl.startsWith('/api/v1')) {
    const path = storageUrl.replace('/api/v1', '')
    const response = await client.get(path, { responseType: 'blob' })
    return URL.createObjectURL(response.data as Blob)
  }
  // Direct Azure Blob URL — opens fine in a new tab / iframe for users who
  // have Azure AD access to the storage account. This is the original
  // pre-#211 behaviour and is safe to use as a fallback: the URL is not
  // exposed to anyone who couldn't already obtain it via the documents
  // list response.
  return storageUrl
}

export const uploadDocument = async (
  applicationId: string,
  file: File,
  kind: string,
): Promise<TapDocument> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('kind', kind)
  const response = await client.post<RawDocument>(
    `/tap/applications/${applicationId}/documents/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return mapDocument(response.data)
}

// ---------------------------------------------------------------------------
// Notes (Feature #160)
// ---------------------------------------------------------------------------

interface RawNote {
  id: string
  application_id: string
  body: string
  author_user_id?: string | null
  // Bug #164: resolved display name for the author so the UI avoids rendering
  // raw UUIDs.
  author_display_name?: string | null
  stage_when_added: string
  is_active: boolean
  created_at: string
}

const mapNote = (r: RawNote): TapNote => ({
  id: r.id,
  applicationId: r.application_id,
  body: r.body,
  authorUserId: r.author_user_id ?? undefined,
  authorDisplayName: r.author_display_name ?? undefined,
  stageWhenAdded: r.stage_when_added,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
})

export const getNotes = async (applicationId: string): Promise<TapNote[]> => {
  const rows = await fetchAllPages<RawNote>(`/tap/applications/${applicationId}/notes`)
  return rows.map(mapNote)
}

// Feature #160: pass `stage_when_added` from the current application stage so
// the backend schema's required field is populated. The server ignores the
// body value and stamps its own from the current `TapApplication.stage`, but
// Pydantic still rejects the request if the field is missing.
export const createNote = async (
  applicationId: string,
  body: string,
  currentStage: string,
): Promise<TapNote> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/notes`,
    {
      application_id: applicationId,
      body,
      stage_when_added: currentStage,
    },
  )
  return mapNote(response.data)
}

// ---------------------------------------------------------------------------
// Skills (library + application associations — Feature #153)
// ---------------------------------------------------------------------------

interface RawSkill {
  id: string
  name: string
  description?: string | null
  is_active: boolean
}

const mapSkill = (r: RawSkill): TapSkill => ({
  id: r.id,
  name: r.name,
  description: r.description ?? undefined,
  isActive: Boolean(r.is_active),
})

export const getSkills = async (): Promise<TapSkill[]> => {
  const rows = await fetchAllPages<RawSkill>('/tap/skills')
  return rows.map(mapSkill)
}

interface RawApplicationSkill {
  id: string
  application_id: string
  skill_id: string
  proficiency?: string | null
  skill_name?: string | null
  is_active: boolean
  created_at: string
}

const mapApplicationSkill = (r: RawApplicationSkill): TapApplicationSkill => ({
  id: r.id,
  applicationId: r.application_id,
  skillId: r.skill_id,
  skillName: r.skill_name ?? undefined,
  proficiency: r.proficiency ?? undefined,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
})

export const getApplicationSkills = async (applicationId: string): Promise<TapApplicationSkill[]> => {
  const rows = await fetchAllPages<RawApplicationSkill>(`/tap/applications/${applicationId}/skills`)
  return rows.map(mapApplicationSkill)
}

export const addApplicationSkill = async (
  applicationId: string,
  skillId: string,
  proficiency?: string,
): Promise<TapApplicationSkill> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/skills`,
    prune({ skill_id: skillId, proficiency }),
  )
  return mapApplicationSkill(response.data)
}

export const updateApplicationSkill = async (
  applicationId: string,
  assocId: string,
  proficiency?: string,
): Promise<TapApplicationSkill> => {
  const response = await client.patch(
    `/tap/applications/${applicationId}/skills/${assocId}`,
    prune({ proficiency }),
  )
  return mapApplicationSkill(response.data)
}

export const removeApplicationSkill = async (
  applicationId: string,
  assocId: string,
): Promise<void> => {
  await client.delete(`/tap/applications/${applicationId}/skills/${assocId}`)
}

// ---------------------------------------------------------------------------
// Certifications (Feature #218)
// ---------------------------------------------------------------------------

interface RawCertification {
  id: string
  application_id: string
  certification_name: string
  issuing_authority?: string | null
  is_active: boolean
  created_at: string
}

const mapCertification = (r: RawCertification): TapApplicationCertification => ({
  id: r.id,
  applicationId: r.application_id,
  certificationName: r.certification_name,
  issuingAuthority: r.issuing_authority ?? undefined,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
})

export const getCertifications = async (
  applicationId: string,
): Promise<TapApplicationCertification[]> => {
  const rows = await fetchAllPages<RawCertification>(`/tap/applications/${applicationId}/certifications`)
  return rows.map(mapCertification)
}

export const addCertification = async (
  applicationId: string,
  data: { certificationName: string; issuingAuthority?: string },
): Promise<TapApplicationCertification> => {
  const response = await client.post(
    `/tap/applications/${applicationId}/certifications`,
    prune({
      certification_name: data.certificationName,
      issuing_authority: data.issuingAuthority,
    }),
  )
  return mapCertification(response.data)
}

export const removeCertification = async (
  applicationId: string,
  certificationId: string,
): Promise<void> => {
  await client.delete(`/tap/applications/${applicationId}/certifications/${certificationId}`)
}

// ---------------------------------------------------------------------------
// Lookup Values (Feature #218)
// ---------------------------------------------------------------------------

interface RawLookupValue {
  id: string
  category: string
  value: string
  description?: string | null
  sort_order?: number | null
  is_active: boolean
}

const mapLookupValue = (r: RawLookupValue): LabLookupValue => ({
  id: r.id,
  category: r.category,
  value: r.value,
  description: r.description ?? undefined,
  sortOrder: r.sort_order ?? undefined,
  isActive: Boolean(r.is_active),
})

export const getLookupValues = async (category: string): Promise<LabLookupValue[]> => {
  const response = await client.get('/lookup-values', { params: { category } })
  return ((response.data || []) as RawLookupValue[]).map(mapLookupValue)
}

// ---------------------------------------------------------------------------
// SLA Settings (existing)
// ---------------------------------------------------------------------------
export const getSlaSettings = async (): Promise<TapStageSla[]> => {
  const rows = await fetchAllPages<TapStageSla>('/tap/settings/stage-sla')
  return rows
}
export const updateSlaSettings = async (stage: string, data: Partial<TapStageSla>): Promise<TapStageSla> => {
  const response = await client.patch(`/tap/settings/stage-sla/${stage}`, data)
  return response.data
}

// ---------------------------------------------------------------------------
// Scorecard Templates (existing)
// ---------------------------------------------------------------------------
export const getScorecardTemplates = async (): Promise<TapScorecardTemplate[]> => {
  const rows = await fetchAllPages<TapScorecardTemplate>('/tap/scorecard-templates')
  return rows
}
export const createScorecardTemplate = async (data: Partial<TapScorecardTemplate>): Promise<TapScorecardTemplate> => {
  const response = await client.post('/tap/scorecard-templates', data)
  return response.data
}
export const getScorecardCompetencies = async (templateId: string): Promise<TapScorecardCompetency[]> => {
  const rows = await fetchAllPages<TapScorecardCompetency>(`/tap/scorecard-templates/${templateId}/competencies`)
  return rows
}

// ---------------------------------------------------------------------------
// Dashboard (existing)
// ---------------------------------------------------------------------------
export const getDashboard = async (params?: { date_from?: string; date_to?: string }): Promise<TapDashboard> => {
  const response = await client.get('/tap/dashboard', { params })
  return response.data
}

// ---------------------------------------------------------------------------
// Feature #229: Rate Calculator settings
// ---------------------------------------------------------------------------

// Wire-format (snake_case) — kept private; UI consumers use TapRateCalculatorSettings.
interface RawRateCalculatorSettings {
  id: string
  fy_label: string
  notes?: string | null
  super_rate: number
  payroll_tax_rate: number
  hours_per_day: number
  weekdays_per_year: number
  annual_leave_days: number
  sick_leave_days: number
  professional_development_days: number
  internal_management_days: number
  utilisation_target_pct: number
  min_tgp_full_time: number
  min_sgp_full_time: number
  min_tgp_part_time: number
  min_sgp_part_time: number
  min_tgp_casual: number
  min_sgp_casual: number
  min_tgp_contractor: number
  min_sgp_contractor: number
  default_billable_days_casual: number
  is_active: boolean
  created_at: string
  updated_at: string
  created_by?: string | null
  updated_by?: string | null
  capacity_days: number
  utilisation_target_days: number
}

export interface TapRateCalculatorSettings {
  id: string
  fyLabel: string
  notes?: string
  superRate: number
  payrollTaxRate: number
  hoursPerDay: number
  weekdaysPerYear: number
  annualLeaveDays: number
  sickLeaveDays: number
  professionalDevelopmentDays: number
  internalManagementDays: number
  utilisationTargetPct: number
  minTgpFullTime: number
  minSgpFullTime: number
  minTgpPartTime: number
  minSgpPartTime: number
  minTgpCasual: number
  minSgpCasual: number
  minTgpContractor: number
  minSgpContractor: number
  defaultBillableDaysCasual: number
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy?: string
  updatedBy?: string
  // Derived (computed server-side):
  capacityDays: number
  utilisationTargetDays: number
}

const mapRateCalculatorSettings = (r: RawRateCalculatorSettings): TapRateCalculatorSettings => ({
  id: r.id,
  fyLabel: r.fy_label,
  notes: r.notes ?? undefined,
  superRate: r.super_rate,
  payrollTaxRate: r.payroll_tax_rate,
  hoursPerDay: r.hours_per_day,
  weekdaysPerYear: r.weekdays_per_year,
  annualLeaveDays: r.annual_leave_days,
  sickLeaveDays: r.sick_leave_days,
  professionalDevelopmentDays: r.professional_development_days,
  internalManagementDays: r.internal_management_days,
  utilisationTargetPct: r.utilisation_target_pct,
  minTgpFullTime: r.min_tgp_full_time,
  minSgpFullTime: r.min_sgp_full_time,
  minTgpPartTime: r.min_tgp_part_time,
  minSgpPartTime: r.min_sgp_part_time,
  minTgpCasual: r.min_tgp_casual,
  minSgpCasual: r.min_sgp_casual,
  minTgpContractor: r.min_tgp_contractor,
  minSgpContractor: r.min_sgp_contractor,
  defaultBillableDaysCasual: r.default_billable_days_casual,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
  updatedAt: r.updated_at,
  createdBy: r.created_by ?? undefined,
  updatedBy: r.updated_by ?? undefined,
  capacityDays: r.capacity_days,
  utilisationTargetDays: r.utilisation_target_days,
})

// Camel→snake on the way out. Used by both create and patch.
const toRateCalcWire = (data: Partial<TapRateCalculatorSettings>): Record<string, unknown> => prune({
  fy_label: data.fyLabel,
  notes: data.notes,
  super_rate: data.superRate,
  payroll_tax_rate: data.payrollTaxRate,
  hours_per_day: data.hoursPerDay,
  weekdays_per_year: data.weekdaysPerYear,
  annual_leave_days: data.annualLeaveDays,
  sick_leave_days: data.sickLeaveDays,
  professional_development_days: data.professionalDevelopmentDays,
  internal_management_days: data.internalManagementDays,
  utilisation_target_pct: data.utilisationTargetPct,
  min_tgp_full_time: data.minTgpFullTime,
  min_sgp_full_time: data.minSgpFullTime,
  min_tgp_part_time: data.minTgpPartTime,
  min_sgp_part_time: data.minSgpPartTime,
  min_tgp_casual: data.minTgpCasual,
  min_sgp_casual: data.minSgpCasual,
  min_tgp_contractor: data.minTgpContractor,
  min_sgp_contractor: data.minSgpContractor,
  default_billable_days_casual: data.defaultBillableDaysCasual,
})

export const listRateCalculatorSettings = async (): Promise<TapRateCalculatorSettings[]> => {
  const response = await client.get('/tap/settings/rate-calculator')
  const items = Array.isArray(response.data) ? response.data : []
  return items.map(mapRateCalculatorSettings)
}

export const getActiveRateCalculatorSettings = async (): Promise<TapRateCalculatorSettings | null> => {
  // 404 means no active row yet — surfacing as null lets the UI render an
  // explanatory empty state rather than treat it as a fetch failure.
  try {
    const response = await client.get('/tap/settings/rate-calculator/active')
    return mapRateCalculatorSettings(response.data)
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status
    if (status === 404) return null
    throw err
  }
}

export const createRateCalculatorSettings = async (
  data: Partial<TapRateCalculatorSettings>,
): Promise<TapRateCalculatorSettings> => {
  const response = await client.post('/tap/settings/rate-calculator', toRateCalcWire(data))
  return mapRateCalculatorSettings(response.data)
}

export const updateRateCalculatorSettings = async (
  id: string,
  data: Partial<TapRateCalculatorSettings>,
): Promise<TapRateCalculatorSettings> => {
  const response = await client.patch(`/tap/settings/rate-calculator/${id}`, toRateCalcWire(data))
  return mapRateCalculatorSettings(response.data)
}

export const activateRateCalculatorSettings = async (id: string): Promise<TapRateCalculatorSettings> => {
  const response = await client.post(`/tap/settings/rate-calculator/${id}/activate`)
  return mapRateCalculatorSettings(response.data)
}

export const deleteRateCalculatorSettings = async (id: string): Promise<void> => {
  await client.delete(`/tap/settings/rate-calculator/${id}`)
}
